import { useQuery } from "@tanstack/react-query";
import { fetchAttemptedGamificationList } from "../services/gamificationService";

const gamificationAttemptColors = ["green", "cyan", "blue", "purple"];

function normalizePagination(pagination = {}, requestedPage = 1, totalRecords = 0) {
  return {
    currentPage: Number(pagination?.currentPage || pagination?.current_page || requestedPage || 1),
    totalPages: Number(pagination?.totalPages || pagination?.total_pages || 1),
    totalRecords: Number(pagination?.totalRecords || pagination?.total_records || totalRecords || 0),
  };
}

function normalizeAttemptItem(item, index, pagination) {
  const currentPage = Number(pagination?.currentPage || 1);
  const totalRecords = Number(pagination?.totalRecords || 0);
  const pageSize = Number(totalRecords > 0 && pagination?.totalPages > 0 ? Math.ceil(totalRecords / pagination.totalPages) : 0) || 10;
  const serial = (currentPage - 1) * pageSize + index + 1;
  const resultKey = String(item?.result_key || "").trim();
  const roomCode = item?.room_code ? `#${item.room_code}` : "-";

  return {
    id: String(serial).padStart(2, "0"),
    battlesId: Number(item?.battles_id || 0),
    battleRoomId: Number(item?.battle_room_id || 0),
    title: item?.battles_name || "Battle",
    battleType: item?.battle_type || "-",
    roomCode,
    attemptedAt: item?.attempted_at || "-",
    status: item?.title || "Pending",
    resultKey,
    rank: item?.user_rank || "0/0",
    score: item?.user_score || "0",
    subjectId: Number(item?.subject_id || 0),
    questionId: Number(item?.question_id || 0),
    questionIndex: Number(item?.question_index || 0),
    action: item?.action || "",
    canRejoin: resultKey === "Rejoin",
    remainingTime: Number(item?.remaining_time || 0),
    coinDelta: item?.coin_delta || "-",
    color: gamificationAttemptColors[index % gamificationAttemptColors.length],
    raw: item,
  };
}

export function useAttemptedGamificationList(params = {}, options = {}) {
  return useQuery({
    queryKey: ["attemptedGamificationList", params],
    queryFn: async () => {
      const { payload } = await fetchAttemptedGamificationList(params);
      return normalizeAttemptedGamificationData(payload, params?.page);
    },
    enabled: options.enabled ?? true,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        categoryName: "Gamification",
        description: "",
        userCoins: 0,
        rank: 0,
        score: 0,
        totalAttemptsLeft: 0,
        gamificationStatus: 0,
        attempts: [],
        pagination: {
          currentPage: Number(params?.page || 1),
          totalPages: 1,
          totalRecords: 0,
        },
      },
  });
}

export function normalizeAttemptedGamificationData(payload = {}, requestedPage = 1) {
  const rawAttempts = Array.isArray(payload?.attempted_gamification_list)
    ? payload.attempted_gamification_list
    : [];
  const pagination = normalizePagination(payload?.pagination || {}, requestedPage, rawAttempts.length);

  return {
    categoryName: payload?.category_name || "Gamification",
    description: payload?.description || "",
    userCoins: Number(payload?.user_coins || 0),
    rank: Number(payload?.rank || 0),
    score: Number(payload?.score || 0),
    totalAttemptsLeft: Number(payload?.total_attempts_left || 0),
    gamificationStatus: Number(payload?.gamification_status || 0),
    attempts: rawAttempts.map((item, index) => normalizeAttemptItem(item, index, pagination)),
    pagination,
  };
}
